import React, { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
  setModuleConfig,
  updateModuleName,
  updateModuleSize,
  updateModuleTechLevel,
} from "../../../store/features/moduleSlice";
import { ModuleSaveLoadButtons } from "../../ModuleSaveLoadButtons";
import { ModuleResetButton } from "../../ModuleResetButton";

import { calculateModuleCost } from "../../../utils/validation/moduleValidation";

/**
 * Module Basics Step Component
 * Handles basic module configuration: name, size, type, tech level
 */
export const ModuleBasicsStep: React.FC = () => {
  const dispatch = useAppDispatch();
  const moduleConfig = useAppSelector((state) => state.module.moduleConfig);

  // Initialize module config if not present
  useEffect(() => {
    if (!moduleConfig) {
      dispatch(setModuleConfig({
        name: "",
        size: 100,
        techLevel: 12,
        description: "",
                  systems: {
            optionalSystems: [],
            powerPlant: [],
            computer: [],
            computerOptions: {},
            software: {
              installed: [
                { packageId: "manoeuvre", level: 0, installed: true },
                { packageId: "intellect", level: 0, installed: true },
                { packageId: "library", level: 0, installed: true },
              ],
              running: [
                { packageId: "manoeuvre", level: 0, running: true },
                { packageId: "intellect", level: 0, running: true },
                { packageId: "library", level: 0, running: true },
              ],
              expertSkills: {},
              translatorLanguages: {},
              databaseTopics: {},
              virtualCrewRoles: {},
              virtualGunnerRoles: {},
            },
          },
        calculations: {
          totalCost: 0,
          totalWeight: 0,
          totalPower: 0,
          remainingTons: 100,
          crewRequirements: 0,
          totalCrew: 0,
          totalPassengers: 0,
          monthlyCost: 0,
        },
      }));
    }
  }, [moduleConfig, dispatch]);

  if (!moduleConfig) {
    return <div className="text-gray-600 dark:text-gray-300">Loading module configuration...</div>;
  }

  const handleNameChange = (name: string) => {
    dispatch(updateModuleName(name));
  };

  const handleSizeChange = (size: number) => {
    dispatch(updateModuleSize(size));
  };

  const handleTechLevelChange = (techLevel: number) => {
    dispatch(updateModuleTechLevel(techLevel));
  };

  const handleDescriptionChange = (description: string) => {
    dispatch(setModuleConfig({
      ...moduleConfig,
      description,
    }));
  };


  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-highlightOrange dark:text-orange-400">
          Module Configuration
        </h2>
        <div className="flex gap-2">
          <ModuleSaveLoadButtons variant="compact" />
          <ModuleResetButton variant="compact" />
        </div>
      </div>


      {/* Module Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Module Name *
        </label>
        <input
          type="text"
          value={moduleConfig.name}
          onChange={(e) => handleNameChange(e.target.value)}
          placeholder="Enter module name..."
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-highlightOrange focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
        />
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Choose a descriptive name for your module
        </p>
      </div>



      {/* Module Size and Tech Level */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Module Size (tons) *
          </label>
          <input
            type="number"
            min="0"
            value={moduleConfig.size}
            onChange={(e) => handleSizeChange(Number(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-highlightOrange focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
          />
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Module size starts at 0 with no upper limit
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Technology Level *
          </label>
          <input
            type="number"
            min="0"
            value={moduleConfig.techLevel}
            onChange={(e) => handleTechLevelChange(Number(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-highlightOrange focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
          />
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Tech Level starts at 0 with no upper limit
          </p>
        </div>
      </div>

      {/* Module Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Description
        </label>
        <textarea
          value={moduleConfig.description || ""}
          onChange={(e) => handleDescriptionChange(e.target.value)}
          placeholder="Enter module description (optional)..."
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-highlightOrange focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
        />
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Optional description of the module's purpose and features
        </p>
      </div>



      {/* Module Summary */}
      <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-3">Module Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="font-medium text-gray-600 dark:text-gray-300">Size:</span>
            <div className="text-gray-900 dark:text-gray-100">{moduleConfig.size} tons</div>
          </div>
          <div>
            <span className="font-medium text-gray-600 dark:text-gray-300">Tech Level:</span>
            <div className="text-gray-900 dark:text-gray-100">{moduleConfig.techLevel}</div>
          </div>
          <div>
            <span className="font-medium text-gray-600 dark:text-gray-300">Module Cost:</span>
            <div className="text-gray-900 dark:text-gray-100">{calculateModuleCost(moduleConfig.size).toLocaleString()} Cr</div>
          </div>
        </div>
      </div>
    </div>
  );
};
